const KEY_TYPE = 'EC';
const CURVE = 'P-256';

export interface DpopPublicKey {
  kty: typeof KEY_TYPE;
  crv: typeof CURVE;
  x: string;
  y: string;
}

export interface DpopKeyPair {
  publicKey: DpopPublicKey;
  // a real CryptoKey, not a JWK: non-extractable, so there is nothing to serialize
  privateKey: CryptoKey;
}

const KEY_ALGORITHM = { name: 'ECDSA', namedCurve: CURVE } as const;
const SIGN_ALGORITHM = { name: 'ECDSA', hash: 'SHA-256' } as const;

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

export function toBase64Url(value: string | ArrayBuffer): string {
  return bytesToBase64Url(
    typeof value === 'string' ? new TextEncoder().encode(value) : new Uint8Array(value),
  );
}

export function isDpopPublicKey(value: unknown): value is DpopPublicKey {
  if (!value || typeof value !== 'object') return false;
  const key = value as Record<string, unknown>;
  return (
    key.kty === KEY_TYPE &&
    key.crv === CURVE &&
    typeof key.x === 'string' &&
    typeof key.y === 'string'
  );
}

function toPublicKey(exported: JsonWebKey): DpopPublicKey {
  if (exported.kty !== KEY_TYPE || exported.crv !== CURVE || !exported.x || !exported.y) {
    throw new Error('invalid dpop public key');
  }
  return { kty: KEY_TYPE, crv: CURVE, x: exported.x, y: exported.y };
}

export async function generateDpopKeyPair(): Promise<DpopKeyPair> {
  // non-extractable, so XSS can use the key but never steal it
  const pair = await crypto.subtle.generateKey(KEY_ALGORITHM, false, ['sign', 'verify']);
  // the public half is always exportable regardless of the flag
  const exportedPublic = await crypto.subtle.exportKey('jwk', pair.publicKey);
  return { publicKey: toPublicKey(exportedPublic), privateKey: pair.privateKey };
}

export async function signWithDpopKey(privateKey: CryptoKey, message: string): Promise<string> {
  const signature = await crypto.subtle.sign(
    SIGN_ALGORITHM,
    privateKey,
    new TextEncoder().encode(message),
  );
  return toBase64Url(signature);
}
